import { useEffect, useState } from 'react';
import { syncedNow } from '@/utils/timesync';

/**
 * Locally interpolated countdown driven by an absolute UTC timestamp.
 * Returns whole seconds remaining (ceil), clamped to [0, max] if max is set.
 * Updates ~5x/s for smooth display.
 */
export function useSyncedCountdown(
    endsAt: number | null | undefined,
    max?: number,
    // 'ceil' (default): whole seconds remaining (last partial second shows 1).
    // 'floor': counts visibly down to 0 (the last <1s shows 0) — use where the
    // timer should reach 0 before the result appears.
    mode: 'ceil' | 'floor' = 'ceil'
): number {
    const round = mode === 'floor' ? Math.floor : Math.ceil;
    const compute = () => {
        if (endsAt == null) return 0;
        const left = Math.max(0, endsAt - syncedNow());
        const sec = round(left / 1000);
        return max != null ? Math.min(sec, max) : sec;
    };

    const [seconds, setSeconds] = useState<number>(compute);

    useEffect(() => {
        setSeconds(compute());
        if (endsAt == null) return;
        const iv = window.setInterval(() => {
            const left = Math.max(0, endsAt - syncedNow());
            const sec = round(left / 1000);
            setSeconds(max != null ? Math.min(sec, max) : sec);
            if (left <= 0) window.clearInterval(iv);
        }, 200);
        return () => window.clearInterval(iv);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endsAt, max, mode]);

    return seconds;
}
